import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useAuth } from './AuthContext';
import {
  clearActiveProjectStorage,
  clearTenantScopedClientState,
  isActiveProjectForCurrentTenant,
  loadActiveProjectForTenant,
  normalizeLegacyActiveProject,
  normalizeTenantSlug,
  persistActiveProjectForTenant,
  TENANT_SESSION_CHANGED_EVENT,
} from '../utils/tenantSessionStorage';

const ActiveProjectContext = createContext(null);

export const useActiveProject = () => useContext(ActiveProjectContext);

export const ActiveProjectProvider = ({ children }) => {
  const { token } = useAuth();

  const [activeProject, setActiveProjectState] = useState(() => {
    if (typeof window === 'undefined') return null;
    const tenant = window.localStorage.getItem('tenant_id');
    const tokenPresent = Boolean(window.localStorage.getItem('token'));
    if (!tokenPresent) {
      clearActiveProjectStorage();
      return null;
    }
    return loadActiveProjectForTenant(tenant);
  });

  const setActiveProject = useCallback((project) => {
    const tenant = normalizeTenantSlug(
      typeof window !== 'undefined' ? window.localStorage.getItem('tenant_id') : null,
    );
    if (project == null) {
      setActiveProjectState(null);
      return;
    }
    if (!tenant) {
      setActiveProjectState(null);
      clearActiveProjectStorage();
      return;
    }
    setActiveProjectState(normalizeLegacyActiveProject({ ...project, _tenantSlug: tenant }));
  }, []);

  const clearActiveProject = useCallback(() => {
    setActiveProjectState(null);
    clearActiveProjectStorage();
  }, []);

  useEffect(() => {
    const tenant = normalizeTenantSlug(
      typeof window !== 'undefined' ? window.localStorage.getItem('tenant_id') : null,
    );
    if (!token || !tenant) {
      setActiveProjectState(null);
      clearActiveProjectStorage();
      return;
    }
    try {
      if (activeProject) {
        if (!isActiveProjectForCurrentTenant(activeProject, tenant)) {
          setActiveProjectState(null);
          clearActiveProjectStorage();
          return;
        }
        persistActiveProjectForTenant(activeProject, tenant);
      } else {
        clearActiveProjectStorage();
      }
    } catch (error) {
      console.error('Error saving active project to localStorage', error);
    }
  }, [activeProject, token]);

  const lastTenantIdRef = useRef(
    typeof window !== 'undefined'
      ? normalizeTenantSlug(window.localStorage.getItem('tenant_id'))
      : null,
  );

  useEffect(() => {
    const currentTenantId = normalizeTenantSlug(
      typeof window !== 'undefined' ? window.localStorage.getItem('tenant_id') : null,
    );
    if (lastTenantIdRef.current !== currentTenantId) {
      lastTenantIdRef.current = currentTenantId;
      setActiveProjectState(null);
      clearActiveProjectStorage();
    }
  });

  useEffect(() => {
    const onTenantSessionChanged = () => {
      setActiveProjectState(null);
      clearActiveProjectStorage();
    };
    window.addEventListener(TENANT_SESSION_CHANGED_EVENT, onTenantSessionChanged);
    return () => {
      window.removeEventListener(TENANT_SESSION_CHANGED_EVENT, onTenantSessionChanged);
    };
  }, []);

  const value = {
    activeProject,
    setActiveProject,
    clearActiveProject,
  };

  return (
    <ActiveProjectContext.Provider value={value}>
      {children}
    </ActiveProjectContext.Provider>
  );
};
